import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import type { CurrentUser } from '../../lib/auth';
import { signOut } from '../../lib/auth';
import { useMe } from '../../lib/useMe';
import { useMyEmployee } from '../../lib/useMyEmployee';
import { usePermissions } from '../../lib/usePermissions';
import { useTimesheetVisibility } from '../../lib/useTimesheetVisibility';
import { Avatar } from '@payforge/ui';
import { ChevronLeftIcon, ChevronRightIcon } from '../../components/icons';
import { ThemePicker } from '../../components/ThemePicker';
import payforgeLogo from '../../assets/payforge-logo.svg';
import payforgeLogoMark from '../../assets/payforge-logo-mark.svg';

// Logo header at the top of the sidebar. Kept OUTSIDE the "Primary navigation"
// landmark so it isn't counted as a nav link. h-14 + bottom border align it with
// the TopBar. Shows the full wordmark when expanded and the compact mark when the
// rail is collapsed (the wordmark can't fit w-14).
function SidebarHeader({ collapsed = false, onNavClick }: { collapsed?: boolean; onNavClick?: () => void }) {
  return (
    <div
      className={`flex h-14 shrink-0 items-center border-b border-slate-200 ${collapsed ? 'justify-center px-2' : 'px-4'}`}
    >
      <NavLink
        to="/"
        end
        aria-label="PayforgeHR home"
        onClick={onNavClick}
        className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        <img
          src={collapsed ? payforgeLogoMark : payforgeLogo}
          alt=""
          className={collapsed ? 'h-9 w-9' : 'h-9 w-auto'}
        />
      </NavLink>
    </div>
  );
}

type Props = {
  user: CurrentUser;
  collapsed: boolean;
  onToggle: () => void;
  mobileNavOpen: boolean;
  onMobileClose: () => void;
};

type NavItem = {
  label: string;
  to: string;
  icon: ReactNode;
};

function HomeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M3 12L12 3l9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 21V12h6v9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 10v11h14V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MyInfoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 9h18M8 2v4M16 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2 20c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M22 20c0-3.314-1.79-6-4-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function FilesIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TimesheetIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', to: '/', icon: <HomeIcon /> },
  { label: 'My Info', to: '/me', icon: <MyInfoIcon /> },
  { label: 'Calendar', to: '/calendar', icon: <CalendarIcon /> },
  { label: 'Timesheets', to: '/timesheet', icon: <TimesheetIcon /> },
  { label: 'People', to: '/people', icon: <PeopleIcon /> },
  { label: 'Files', to: '/files', icon: <FilesIcon /> },
];

// When collapsed the link holds only the icon, so center it and drop the
// left padding; expanded it keeps the icon+label row with px-3.
function railItemClass(collapsed: boolean) {
  return ({ isActive }: { isActive: boolean }): string =>
    `flex items-center gap-3 rounded-md py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
      collapsed ? 'justify-center px-0' : 'px-3'
    } ${isActive ? 'bg-brand-600 text-fg-inverse' : 'text-slate-700 hover:bg-brand-50'}`;
}

type NavContentProps = {
  collapsed: boolean;
  onToggle: () => void;
  user: CurrentUser;
  onNavClick?: () => void;
};

function NavContent({ collapsed, onToggle, user, onNavClick }: NavContentProps) {
  const me = useMe();
  const myEmployee = useMyEmployee();
  const timesheetVisibility = useTimesheetVisibility();
  const permissions = usePermissions();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const avatarRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const effectiveName = me.data?.effectiveDisplayName ?? user.displayName;
  const effectiveEmail = user.workEmail;

  const photoUrl = myEmployee.data?.profilePictureUrl;

  useEffect(() => {
    if (!popoverOpen) return;

    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (
        avatarRef.current && !avatarRef.current.contains(target) &&
        popoverRef.current && !popoverRef.current.contains(target)
      ) {
        setPopoverOpen(false);
      }
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setPopoverOpen(false);
        avatarRef.current?.focus();
      }
    }

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [popoverOpen]);

  async function handleSignOut() {
    await signOut();
    window.location.assign('/login');
  }

  function handleNavItemClick() {
    onNavClick?.();
  }

  // Timesheet is hidden entirely unless the feature is visible to this user (OFF for everyone,
  // ADMIN_ONLY for non-admins). While the flag is loading we hide it rather than flash-then-hide.
  // Files mirrors the backend files_page_access module gate the same way.
  const navItems = NAV_ITEMS.filter(
    (item) =>
      (item.to !== '/timesheet' || timesheetVisibility.data?.canView === true) &&
      (item.to !== '/files' || permissions.moduleEnabled('files_page_access')),
  );

  return (
    <>
      {/* Nav items */}
      <ul className="mt-3 space-y-0.5 px-2 flex-1">
        {navItems.map((item) =>
          item.to === '/' ? (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end
                className={railItemClass(collapsed)}
                aria-label={item.label}
                title={collapsed ? item.label : undefined}
                onClick={handleNavItemClick}
              >
                <span className="shrink-0">{item.icon}</span>
                {!collapsed ? <span>{item.label}</span> : null}
              </NavLink>
            </li>
          ) : (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={railItemClass(collapsed)}
                aria-label={item.label}
                title={collapsed ? item.label : undefined}
                onClick={handleNavItemClick}
              >
                <span className="shrink-0">{item.icon}</span>
                {!collapsed ? <span>{item.label}</span> : null}
              </NavLink>
            </li>
          ),
        )}
      </ul>

      {/* My Account anchored bottom */}
      <div className="mt-auto px-2 pb-2 relative">
        <hr className="mb-2 border-slate-200" />

        {/* Popover */}
        {popoverOpen ? (
          <div
            ref={popoverRef}
            role="menu"
            // `fixed` (not `absolute`) so the menu escapes the sidebar <nav>'s
            // overflow-y-auto, which otherwise clips it horizontally when the rail
            // is collapsed (an `auto` axis forces the other axis to clip too).
            className="fixed bottom-16 left-2 z-50 w-52 rounded-xl border border-slate-200 bg-surface py-2"
            style={{ maxHeight: 'calc(100dvh - 8rem)', overflowY: 'auto' }}
          >
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-900">{effectiveName}</p>
              <p className="text-xs text-slate-500 truncate">{effectiveEmail}</p>
            </div>
            <NavLink
              to="/me"
              role="menuitem"
              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              onClick={() => {
                setPopoverOpen(false);
                onNavClick?.();
              }}
            >
              My Info
            </NavLink>
            <div className="my-1 border-t border-slate-100" />
            <ThemePicker />
            <div className="my-1 border-t border-slate-100" />
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              onClick={() => {
                setPopoverOpen(false);
                void handleSignOut();
              }}
            >
              Sign out
            </button>
          </div>
        ) : null}

        {/* When collapsed, the expand chevron gets its own centered row ABOVE the avatar */}
        {collapsed ? (
          <button
            type="button"
            aria-label="Expand navigation"
            onClick={onToggle}
            className="mb-1 flex w-full items-center justify-center rounded-md px-2 py-1.5 text-slate-500 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        ) : null}

        {/* Avatar / My Account button, with the collapse chevron to its right */}
        <div className="flex items-center gap-1">
          <button
            ref={avatarRef}
            type="button"
            aria-label="My account"
            aria-haspopup="menu"
            aria-expanded={popoverOpen}
            onClick={() => setPopoverOpen((v) => !v)}
            className={`flex items-center gap-3 rounded-md py-2 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
              collapsed ? 'w-full justify-center px-0' : 'flex-1 px-2'
            }`}
          >
            <Avatar name={effectiveName} photoUrl={photoUrl} size={32} variant="brand" />
            {!collapsed ? (
              <span className="truncate text-sm font-medium text-slate-700">
                My Account
              </span>
            ) : null}
          </button>

          {/* Collapse toggle — only beside the account row when expanded */}
          {!collapsed ? (
            <button
              type="button"
              aria-label="Collapse navigation"
              onClick={onToggle}
              className="shrink-0 rounded-md p-1.5 text-slate-500 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}

export function LeftNav({ user, collapsed, onToggle, mobileNavOpen, onMobileClose }: Props) {
  const navWidth = collapsed ? 'w-14' : 'w-56';
  const drawerRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!mobileNavOpen) return;

    // Move focus into drawer on open
    const drawer = drawerRef.current;
    if (drawer) {
      const focusable = drawer.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      firstFocusableRef.current = focusable;
      focusable?.focus();
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onMobileClose();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen, onMobileClose]);

  return (
    <>
      {/* Desktop rail — full height, hidden below md. Plain layout container (not <aside>): the
          primary-navigation landmark is the inner <nav>, and the page's only `complementary`
          landmark must remain the profile ProfileSidebar. The logo header sits above the
          navigation landmark so it never counts as a nav link. */}
      <div
        className={`${navWidth} shrink-0 hidden md:flex flex-col bg-surface sticky top-0 self-start h-[100dvh] transition-all duration-200 z-10`}
      >
        <SidebarHeader collapsed={collapsed} />
        {/* Right divider drawn as an inset shadow rather than border-r: a border would consume 1px of
            the nav's border-box width (56->55px), shrinking the collapsed rail buttons to 39px and
            pulling their centre to 27.5 while the borderless logo header stays at 28. The shadow
            paints the same 1px line without stealing width, so buttons stay 40px and every icon +
            the logo share the true rail centre (28). */}
        {/* The sidebar's right-hand hairline. Was a hard-coded #e2e8f0 inset shadow, which stays
            light-grey in dark mode; a real right border picks up the themed edge token. */}
        <nav aria-label="Primary navigation" className="flex flex-1 flex-col overflow-y-auto border-r border-slate-200">
          <NavContent collapsed={collapsed} onToggle={onToggle} user={user} />
        </nav>
      </div>

      {/* Mobile drawer — visible below md when mobileNavOpen */}
      {mobileNavOpen ? (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-30 bg-black/40 md:hidden"
            aria-hidden
            onClick={onMobileClose}
          />
          {/* Drawer */}
          <div
            ref={drawerRef}
            className="fixed inset-y-0 left-0 z-40 w-72 max-w-[85vw] bg-surface flex flex-col overflow-y-auto md:hidden"
          >
            <SidebarHeader onNavClick={onMobileClose} />
            <nav aria-label="Primary navigation" className="flex flex-1 flex-col">
              <NavContent collapsed={false} onToggle={onToggle} user={user} onNavClick={onMobileClose} />
            </nav>
          </div>
        </>
      ) : null}
    </>
  );
}
